// Detail shape of GET /asset-transactions/:id and POST /asset-transactions
// (at__transaction_detail): header (to_jsonb of the row plus joined labels,
// pattern/type behaviour flags and currency context), the journal set
// (default preview or user-customized), totals, and the chart-of-accounts
// availability flags. Row objects are passthrough while pinning the fields
// the UI consumes.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const AssetTransactionHeaderSchema = z
  .object({
    id: z.string(),
    type_id: z.number(),
    company: z.string(),
    name: z.string().nullish(),
    transaction_date: z.string().nullish(),
    late_booking: z.boolean().nullish(),
    first_report_date: z.string().nullish(),
    deal: z.string().nullish(),
    notes: z.string().nullish(),
    fund: z.string().nullish(),
    all_funds: z.boolean().nullish(),
    warehoused: z.boolean().nullish(),
    // an acquisition that took a WAREHOUSED position over from another fund
    // names the sending fund's Disposal (a transfer between funds, 2026-11-05)
    transfer_from_transaction: z.string().nullish(),
    security: z.string().nullish(),
    new_security: z.string().nullish(),
    tranche_targeted: z.string().nullish(),
    tranche_created: z.string().nullish(),
    scope_id: z.number().nullish(),
    valuation_type_id: z.number().nullish(),
    security_type_id: z.number().nullish(),
    currency_id: z.string().nullish(),
    currency_functional_id: z.string().nullish(),
    amount: z.number().nullish(),
    amount_functional: z.number().nullish(),
    no_acquired: z.number().nullish(),
    principal_acquired: z.number().nullish(),
    no_disposed: z.number().nullish(),
    principal_disposed: z.number().nullish(),
    split_factor: z.number().nullish(),
    perc_cost_allocated: z.number().nullish(),
    accounting_treatment_id: z.number().nullish(),
    price: z.number().nullish(),
    price_functional: z.number().nullish(),
    escrow_declared: z.number().nullish(),
    escrow_declared_functional: z.number().nullish(),
    escrow_paid: z.number().nullish(),
    escrow_paid_functional: z.number().nullish(),
    escrow_reduced: z.number().nullish(),
    escrow_reduced_functional: z.number().nullish(),
    // a conversion under a uses_fair_value treatment: the total fair value
    // of the securities received, transaction currency and functional
    // (asset-delta design PR 2, 2026-10-21)
    fair_value: z.number().nullish(),
    fair_value_functional: z.number().nullish(),
    additional_payment: z.boolean().nullish(),
    // interest (docs/INTEREST_ACCRUAL_DESIGN.md, PR B): a conversion's
    // capitalised-interest override (security currency; null = the engine's
    // figure) and an accrual's period end
    interest_capitalized_override: z.number().nullish(),
    interest_period_end: z.string().nullish(),
    fx_rate: z.number().nullish(),
    specify_fx_rate: z.boolean().nullish(),
    use_fx_rate: z.boolean().nullish(),
    status_id: z.number().nullish(),
    journals_customized: z.boolean().nullish(),
    trashed: z.boolean().nullish(),
    // joined labels + cfg behaviour flags (at__transaction_detail)
    type_label: z.string().nullish(),
    status_label: z.string().nullish(),
    pattern_label: z.string().nullish(),
    pattern_tranche_created: z.boolean().nullish(),
    pattern_tranche_targeted: z.boolean().nullish(),
    pattern_has_scope: z.boolean().nullish(),
    pattern_countable_only: z.boolean().nullish(),
    pattern_targets_existing: z.boolean().nullish(),
    pattern_uses_price: z.boolean().nullish(),
    pattern_uses_split_factor: z.boolean().nullish(),
    pattern_uses_units_disposed: z.boolean().nullish(),
    pattern_uses_interest_capitalization: z.boolean().nullish(),
    pattern_uses_interest_period_end: z.boolean().nullish(),
    amount_label: z.string().nullish(),
    has_additional_payment: z.boolean().nullish(),
    exercisable_only: z.boolean().nullish(),
    escrow_column: z.string().nullish(),
    journals_separate_tab: z.boolean().nullish(),
    has_journal_formulas: z.boolean().nullish(),
    company_name: z.string().nullish(),
    fund_name: z.string().nullish(),
    deal_name: z.string().nullish(),
    security_name: z.string().nullish(),
    new_security_name: z.string().nullish(),
    tranche_targeted_name: z.string().nullish(),
    functional_currency_id: z.string().nullish(),
    functional_currency_symbol: z.string().nullish(),
    functional_currency_code: z.string().nullish(),
    currency_code: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    security_currency_id: z.string().nullish(),
    security_currency_symbol: z.string().nullish(),
    security_is_countable: z.boolean().nullish(),
    new_security_currency_id: z.string().nullish(),
    new_security_currency_symbol: z.string().nullish(),
    new_security_is_countable: z.boolean().nullish(),
    tranche_security_currency_id: z.string().nullish(),
    tranche_security_currency_symbol: z.string().nullish(),
    tranche_security_is_countable: z.boolean().nullish()
  })
  .passthrough()

export const AssetTransactionJournalSchema = z
  .object({
    id: z.string(),
    journal_date: z.string().nullish(),
    item_id: z.string().nullish(),
    item_label: z.string().nullish(),
    debit_credit_id: z.string(),
    amount: z.number(),
    description: z.string().nullish(),
    asset_transaction_id: z.string().nullish(),
    tranche_id: z.string().nullish(),
    tranche_name: z.string().nullish()
  })
  .passthrough()

export const AssetTransactionTotalsSchema = z
  .object({
    debits: z.number(),
    credits: z.number(),
    difference: z.number()
  })
  .passthrough()

// Valuation transactions: explicit unit values per in-scope functional
// currency that differs from the quote currency (multi-currency fan-out).
export const AssetTransactionValuationValueSchema = z
  .object({
    currency_functional_id: z.string(),
    currencies__code: z.string().nullish(),
    unit_value_functional: z.number()
  })
  .passthrough()

// view__valuation_scope_currencies row: a functional currency of funds
// holding in-scope positions as-of the transaction date.
export const ValuationScopeCurrencySchema = z
  .object({
    currency_id: z.string(),
    code: z.string(),
    symbol: z.string().nullish(),
    funds: z.array(z.string().nullable()).optional().default([])
  })
  .passthrough()

// The accrued-interest position behind a transaction that targets a tranche
// whose security carries interest terms (at__transaction_detail.interest;
// docs/INTEREST_ACCRUAL_DESIGN.md sections 6.2 and 6.3): the terms, the
// engine's calculated accrual, the booked balance and the interest earned
// strictly before the transaction, and - on a conversion - what it
// capitalises and the true-up it books. null for every other transaction. A
// read of the calculation: nothing here books.
export const AssetTransactionInterestSchema = z
  .object({
    tranche_id: z.string().nullish(),
    tranche_name: z.string().nullish(),
    security_currency_code: z.string().nullish(),
    security_currency_symbol: z.string().nullish(),
    interest_rate: z.number().nullish(),
    interest_rate_type_label: z.string().nullish(),
    compounds: z.boolean().nullish(),
    compounding_frequency_label: z.string().nullish(),
    day_count_label: z.string().nullish(),
    maturity_date: z.string().nullish(),
    accrues_after_maturity: z.boolean().nullish(),
    interest_accrues_from: z.string().nullish(),
    interest_payment_type_label: z.string().nullish(),
    // the engine: at the close of the transaction date, and up to (not
    // including) it
    calculated_to_date: z.number().nullish(),
    calculated_before: z.number().nullish(),
    // the score, strictly before the transaction
    booked_balance: z.number().nullish(),
    booked_balance_functional: z.number().nullish(),
    booked_earned: z.number().nullish(),
    units_held_before: z.number().nullish(),
    // a conversion: what this row capitalises and the true-up it books
    capitalized: z.number().nullish(),
    capitalized_functional: z.number().nullish(),
    trueup: z.number().nullish(),
    trueup_functional: z.number().nullish()
  })
  .passthrough()

export const AssetTransactionDetailSchema = z
  .object({
    transaction: AssetTransactionHeaderSchema,
    journals: z.array(AssetTransactionJournalSchema),
    valuation_values: z.array(AssetTransactionValuationValueSchema).optional().default([]),
    interest: AssetTransactionInterestSchema.nullish(),
    totals: AssetTransactionTotalsSchema,
    fund_has_coa: z.boolean().nullish(),
    funds_missing_coa: z.array(z.string()).optional().default([])
  })
  .strict()

export type AssetTransactionHeader = z.infer<typeof AssetTransactionHeaderSchema>
export type AssetTransactionJournal = z.infer<typeof AssetTransactionJournalSchema>
export type AssetTransactionValuationValue = z.infer<typeof AssetTransactionValuationValueSchema>
export type AssetTransactionInterest = z.infer<typeof AssetTransactionInterestSchema>
export type ValuationScopeCurrency = z.infer<typeof ValuationScopeCurrencySchema>
export type AssetTransactionTotals = z.infer<typeof AssetTransactionTotalsSchema>
export type AssetTransactionDetail = z.infer<typeof AssetTransactionDetailSchema>

export function validateAssetTransactionDetail(data: unknown): AssetTransactionDetail {
  try {
    return validateWithZodSchema(AssetTransactionDetailSchema, data, 'asset transaction detail')
  } catch (error) {
    throw new Error(`Asset transaction detail validation failed: ${error}`)
  }
}

/** Save payload sent to POST /asset-transactions (at__save_transaction). */
export interface AssetTransactionSavePayload {
  id?: string
  type_id?: number
  company?: string
  name?: string | null
  transaction_date?: string
  late_booking?: boolean
  first_report_date?: string | null
  deal?: string | null
  notes?: string | null
  fund?: string | null
  all_funds?: boolean
  warehoused?: boolean
  transfer_from_transaction?: string | null
  security?: string | null
  new_security?: string | null
  tranche_targeted?: string | null
  scope_id?: number | null
  valuation_type_id?: number | null
  security_type_id?: number | null
  currency_id?: string | null
  amount?: number | null
  amount_functional?: number | null
  no_acquired?: number | null
  principal_acquired?: number | null
  no_disposed?: number | null
  principal_disposed?: number | null
  split_factor?: number | null
  perc_cost_allocated?: number | null
  accounting_treatment_id?: number | null
  price?: number | null
  price_functional?: number | null
  escrow_declared?: number | null
  escrow_declared_functional?: number | null
  escrow_paid?: number | null
  escrow_paid_functional?: number | null
  escrow_reduced?: number | null
  escrow_reduced_functional?: number | null
  /** A conversion under a Fair value treatment: the total fair value received, and its functional twin. */
  fair_value?: number | null
  fair_value_functional?: number | null
  additional_payment?: boolean
  /** A conversion: the capitalised interest, security currency; null = the engine's figure. */
  interest_capitalized_override?: number | null
  /** An accrual: the date it accrues "to". */
  interest_period_end?: string | null
  fx_rate?: number | null
  specify_fx_rate?: boolean
  use_fx_rate?: boolean
  /** Full replacement set; presence marks the journals user-customized. */
  journals?: Array<{
    id?: string
    acc_item_id: string
    debit_credit_id: 'dr' | 'cr'
    amount: number
    description?: string | null
    tranche_id?: string | null
  }>
  valuation_values?: Array<{
    currency_functional_id: string
    unit_value_functional: number
  }>
}

/** Option row shapes of GET /asset-transactions/options and /company-options. */
export const AssetTransactionSecurityOptionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    currency_id: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    currency_code: z.string().nullish(),
    valuation_type_id: z.number().nullish(),
    is_countable: z.boolean().nullish(),
    is_exercisable: z.boolean().nullish(),
    // key terms (shown in the dropdown / summary line)
    security_type_label: z.string().nullish(),
    issue_price: z.number().nullish(),
    nominal_value: z.number().nullish(),
    interest_rate: z.number().nullish(),
    maturity_date: z.string().nullish(),
    valuation_cap: z.number().nullish(),
    conversion_discount: z.number().nullish(),
    strike_price: z.number().nullish()
  })
  .passthrough()

/** Payload for POST /asset-transactions/securities (at__create_security). */
export interface CreateSecurityPayload {
  name: string
  issuer: string
  type_id: number
  currency_id: string
  valuation_type_id?: number | null
  issue_price?: number | null
  nominal_value?: number | null
  interest_rate?: number | null
  interest_rate_type_id?: number | null
  compounding_frequency_id?: number | null
  day_count_convention_id?: number | null
  maturity_date?: string | null
  maturity_months?: number | null
  valuation_cap_type_id?: number | null
  valuation_cap?: number | null
  valuation_floor?: number | null
  conversion_discount?: number | null
  strike_price?: number | null
  notes?: string | null
}

export const AssetTransactionTrancheOptionSchema = z
  .object({
    value: z.string(),
    label: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    securities__id: z.string().nullish(),
    securities__name: z.string().nullish(),
    security_currency_id: z.string().nullish(),
    security_currency_symbol: z.string().nullish(),
    security_currency_code: z.string().nullish(),
    valuation_type_id: z.number().nullish(),
    is_countable: z.boolean().nullish(),
    is_exercisable: z.boolean().nullish(),
    date_invested: z.string().nullish(),
    date_invested_fmt: z.string().nullish(),
    date_exited: z.string().nullish(),
    date_exited_fmt: z.string().nullish(),
    // false = fully exited/wound up as-of the date — still selectable for
    // post-exit bookings (distributions, escrow, late corrections)
    is_open: z.boolean().nullish(),
    no_held: z.number().nullish(),
    principal_held: z.number().nullish(),
    cost_held: z.number().nullish(),
    // as-of fair value — drives the relative-FV default for cost %
    // allocated on securities distributions
    fmv: z.number().nullish(),
    // remaining escrow balance (fund-functional) — position-strip display
    // and the Escrow Received amount default
    escrow_balance: z.number().nullish(),
    currency_symbol: z.string().nullish()
  })
  .passthrough()

export const AssetTransactionHeaderOptionsSchema = z
  .object({
    companies: z.array(z.object({ value: z.string(), label: z.string() }).passthrough()).default([]),
    deals: z
      .array(
        z
          .object({
            value: z.string(),
            label: z.string(),
            account_id: z.string().nullish(),
            round_id: z.string().nullish(),
            round_security_type__label: z.string().nullish()
          })
          .passthrough()
      )
      .default([])
  })
  .strict()

// A Disposal on one of the company's WAREHOUSED tranches: what an acquisition
// in another fund names as "Transferred from" (view__asset_transaction_company_options
// transfer_sources, 2026-11-05).
export const AssetTransactionTransferSourceOptionSchema = z
  .object({
    value: z.string(),
    label: z.string().nullish(),
    funds__id: z.string().nullish(),
    funds__name: z.string().nullish(),
    tranches__id: z.string().nullish(),
    tranches__name: z.string().nullish(),
    transaction_date: z.string().nullish(),
    amount: z.number().nullish()
  })
  .passthrough()

export const AssetTransactionCompanyOptionsSchema = z
  .object({
    securities: z.array(AssetTransactionSecurityOptionSchema).default([]),
    all_securities: z.array(AssetTransactionSecurityOptionSchema).default([]),
    tranches: z.array(AssetTransactionTrancheOptionSchema).default([]),
    transfer_sources: z.array(AssetTransactionTransferSourceOptionSchema).default([])
  })
  .strict()

export type AssetTransactionSecurityOption = z.infer<typeof AssetTransactionSecurityOptionSchema>
export type AssetTransactionTrancheOption = z.infer<typeof AssetTransactionTrancheOptionSchema>
export type AssetTransactionTransferSourceOption = z.infer<typeof AssetTransactionTransferSourceOptionSchema>
export type AssetTransactionHeaderOptions = z.infer<typeof AssetTransactionHeaderOptionsSchema>
export type AssetTransactionCompanyOptions = z.infer<typeof AssetTransactionCompanyOptionsSchema>

export function validateAssetTransactionHeaderOptions(data: unknown): AssetTransactionHeaderOptions {
  try {
    return validateWithZodSchema(AssetTransactionHeaderOptionsSchema, data, 'asset transaction header options')
  } catch (error) {
    throw new Error(`Asset transaction header options validation failed: ${error}`)
  }
}

export function validateAssetTransactionCompanyOptions(data: unknown): AssetTransactionCompanyOptions {
  try {
    return validateWithZodSchema(AssetTransactionCompanyOptionsSchema, data, 'asset transaction company options')
  } catch (error) {
    throw new Error(`Asset transaction company options validation failed: ${error}`)
  }
}
